import React, {
  createContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { apiService } from '@/services/api';

// Auto-refresh interval in milliseconds (1 minute)
const NOTIFICATION_REFRESH_INTERVAL = 30000;

// Helper function to convert API notification to UI notification
const convertApiNotificationToUI = (apiNotification: NotificationApiItem): NotificationItem => {
  let title = '';
  let subtitle = '';

  switch (apiNotification.type) {
    case 'ticket_created':
      title = 'New ticket created';
      subtitle = 'A new support ticket has been created';
      break;
    case 'ticket_message_added':
      title = 'New message in ticket';
      subtitle = `A new message was added to ticket #${apiNotification.entityId}`;
      break;
    default:
      title = 'Notification';
      subtitle = 'You have a new notification';
  }

  return {
    id: apiNotification.id,
    entityId: apiNotification.entityId,
    entityType: apiNotification.entityType,
    type: apiNotification.type,
    title,
    subtitle,
    createdAt: apiNotification.createdAt,
    isRead: apiNotification.isRead,
    readAt: apiNotification.readAt,
  };
};

// Define notification types from API
export type NotificationEntityType = 'ticket';
export type NotificationType = 'ticket_created' | 'ticket_message_added';

// API response interfaces
export interface NotificationApiItem {
  id: string;
  entityId: string;
  entityType: NotificationEntityType;
  type: NotificationType;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsApiResponse {
  notifications: NotificationApiItem[];
  unreadCount: number;
}

// UI notification interface
export interface NotificationItem {
  id: string;
  entityId: string;
  entityType: NotificationEntityType;
  type: NotificationType;
  title: string;
  subtitle: string;
  createdAt: string;
  isRead: boolean;
  readAt: string | null;
}

// Define context props interface
export interface NotificationContextProps {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string;
  setNotifications: Dispatch<SetStateAction<NotificationItem[]>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string>>;
  loadNotifications: (showLoading?: boolean) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => void;
  getNotificationLink: (notification: NotificationItem) => string;
}

// Create the context with default values
export const NotificationContext = createContext<NotificationContextProps>({
  notifications: [],
  unreadCount: 0,
  loading: true,
  error: '',
  setNotifications: () => {},
  setLoading: () => {},
  setError: () => {},
  loadNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: () => {},
  getNotificationLink: () => '',
});

// Create the provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Calculate unread count
  const unreadCount = notifications.filter(notification => !notification.isRead).length;

  // Function to load notifications from API
  const loadNotifications = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      
      const response = await apiService.getNotifications();
      
      // Convert API notifications to UI format
      const uiNotifications = response.notifications.map(convertApiNotificationToUI);
      
      setNotifications(uiNotifications);
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
      console.error('Error loading notifications:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Function to mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      // Send API request first
      await apiService.markNotificationAsRead(notificationId);
      
      // If successful, remove notification from list
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      // On error, just mark as read locally without removing
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date().toISOString() }
            : notification,
        ),
      );
    }
  };

  // Function to mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, isRead: true })),
    );
  };

  // Function to generate link based on notification type
  const getNotificationLink = (notification: NotificationItem): string => {
    switch (notification.entityType) {
      case 'ticket':
        return `/tickets${notification.entityId ? `/${notification.entityId}` : ''}`;
      default:
        return '/dashboard';
    }
  };

  // Auto-refresh interval ref
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load notifications on mount and set up auto-refresh
  useEffect(() => {
    // Initial load with loading indicator
    loadNotifications(true);

    // Set up auto-refresh
    refreshIntervalRef.current = setInterval(() => {
      // Auto-refresh without loading indicator
      loadNotifications(false);
    }, NOTIFICATION_REFRESH_INTERVAL);

    // Handle visibility change - pause/resume auto-refresh when tab is hidden/visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - clear interval
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      } else {
        // Tab is visible - resume interval and refresh immediately
        loadNotifications(false);
        
        if (!refreshIntervalRef.current) {
          refreshIntervalRef.current = setInterval(() => {
            loadNotifications(false);
          }, NOTIFICATION_REFRESH_INTERVAL);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const contextValue: NotificationContextProps = {
    notifications,
    unreadCount,
    loading,
    error,
    setNotifications,
    setLoading,
    setError,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    getNotificationLink,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
